/** /notifications/blocks — cross-tenant Notification_Block catalog (grid +
 *  create/edit dialog). A block is a named, typed value referenced in
 *  templates as `block.<codename>`; a null tenant is a system-wide block.
 *  Filters (operator scope / type / enabled) and search are held in local state
 *  and passed to the list query as `extra`. */
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Button, MenuItem, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import * as blocksApi from '@/api/notificationBlocks';
import {
  BLOCK_TYPES,
  type NotificationBlockListItem,
  type NotificationBlockType,
} from '@/api/notificationBlocks';
import { deleteBatchAction } from '@/components/DataGrid/batchTypes';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import {
  pickedSentinel,
  TenantPicker,
  type TenantPickerValue,
} from '@/components/TenantPicker/TenantPicker';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { useEditIntent } from '@/hooks/useEditIntent';

import { BlockProvenanceChips } from './BlockProvenanceChips';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds } from '@/scope/tenantIds';
import { useScopedTenantsState } from '@/scope/useScopedTenantsState';

import { NotificationBlockFormDialog } from './NotificationBlockFormDialog';

/** Sentinel picker option: system-wide blocks (`tour_operator IS NULL`). */
const SYSTEM_TENANT: TenantPickerValue = { id: 0, name: 'System / global (no operator)' };
const SYSTEM_EXTRA_OPTIONS = [SYSTEM_TENANT];

const TYPE_LABEL: Record<string, string> = Object.fromEntries(
  BLOCK_TYPES.map((t) => [t.value, t.label]),
);

export function NotificationBlocksListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_notification_blocks_manage');

  // Dialog: null block + open → create; a fetched detail → edit.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  // Filters (local state → list `extra`). The operators (several, D1) or the
  // System sentinel alone (D3); they follow the console scope until picked here.
  const [tenants, setTenants] = useScopedTenantsState();
  const [typeFilter, setTypeFilter] = useState<NotificationBlockType | ''>('');
  const [enabledFilter, setEnabledFilter] = useState<'any' | 'true' | 'false'>('any');

  const tenantIds = formatTenantIds(tenants.map((t) => t.id));
  const tenantParam = pickedSentinel(tenants, SYSTEM_EXTRA_OPTIONS) ? 'system' : tenantIds;

  const anyFilterActive = tenants.length > 0 || typeFilter !== '' || enabledFilter !== 'any';
  const resetAllFilters = () => {
    setTenants([]);
    setTypeFilter('');
    setEnabledFilter('any');
  };

  const extra = useMemo(() => {
    const e: Record<string, string | number | boolean> = {};
    if (tenantParam) e.tour_operator_id = tenantParam;
    if (typeFilter) e.type = typeFilter;
    if (enabledFilter !== 'any') e.enabled = enabledFilter === 'true';
    return e;
  }, [tenantParam, typeFilter, enabledFilter]);

  // The edit dialog needs the full Detail; fetch it on demand.
  const editQuery = useQuery({
    queryKey: ['notification_blocks', 'detail', editId],
    queryFn: () => blocksApi.retrieve(editId as number),
    enabled: editId !== null,
  });

  // The create dialog features the page's operators and pre-selects the one when
  // there is exactly one (D9); the System sentinel (id 0) names no operator.
  const pageTenants = pageTenantsFrom(tenantIds, tenants);

  useCreateIntent(() => openCreate());

  const openCreate = () => {
    setEditId(null);
    setDialogOpen(true);
  };
  const openEdit = useCallback((id: number) => {
    setEditId(id);
    setDialogOpen(true);
  }, []);
  useEditIntent(openEdit);

  const columns = useMemo<GridColDef<NotificationBlockListItem>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      {
        field: 'codename',
        headerName: 'Codename',
        flex: 1,
        minWidth: 200,
        renderCell: (p) =>
          canManage ? (
            <Box
              component="button"
              type="button"
              onClick={() => openEdit(p.row.id)}
              sx={{
                background: 'none',
                border: 'none',
                p: 0,
                cursor: 'pointer',
                color: '#0a1a3f',
                fontWeight: 500,
                font: 'inherit',
                textAlign: 'left',
              }}
            >
              {p.row.codename}
            </Box>
          ) : (
            p.row.codename
          ),
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
        sortable: false,
        valueGetter: (_, row) => row.name ?? '',
      },
      {
        // Not sortable: both values are computed per row rather than stored, so
        // a sort header here would be a dead control (the server has nothing to
        // order by).
        field: 'provenance',
        headerName: 'Provenance',
        width: 190,
        sortable: false,
        filterable: false,
        renderCell: (p) => <BlockProvenanceChips block={p.row} />,
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 160,
        valueGetter: (_, row) => TYPE_LABEL[row.type] ?? row.type,
      },
      {
        // Which blocks are feature-gated — the 2026-07-29 feature-flag mechanism was
        // invisible in the grid. Already serialized; null = unconditional.
        field: 'feature',
        headerName: 'Feature',
        width: 160,
        sortable: false,
        valueGetter: (_, row) => row.feature ?? '—',
      },
      {
        // soft = log + continue; hard = abort delivery on error. Safety-relevant.
        field: 'error_mode',
        headerName: 'On error',
        width: 110,
        sortable: false,
        valueGetter: (_, row) => row.error_mode,
      },
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        valueGetter: (_, row) => row.tenant?.name ?? 'System',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} empty="System" />,
      },
      {
        field: 'enabled',
        headerName: 'Enabled',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        renderCell: (p) =>
          p.value ? <CheckIcon fontSize="small" color="success" aria-label="yes" /> : null,
      },
    ],
    [canManage, openEdit],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      blocksApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? 'codename',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'scope',
      priority: 5,
      active: tenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={tenants}
            onChange={setTenants}
            label="Operator"
            extraOptions={SYSTEM_EXTRA_OPTIONS}
          />
        </Box>
      ),
    },
    {
      key: 'type',
      priority: 4,
      active: typeFilter !== '',
      node: (
        <TextField
          select
          size="small"
          label="Type"
          value={typeFilter}
          sx={{ width: 190 }}
          onChange={(e) => setTypeFilter(e.target.value as NotificationBlockType | '')}
        >
          <MenuItem value="">Any type</MenuItem>
          {BLOCK_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      key: 'enabled',
      priority: 3,
      active: enabledFilter !== 'any',
      node: (
        <TextField
          select
          size="small"
          label="Enabled"
          value={enabledFilter}
          sx={{ width: 160 }}
          onChange={(e) => setEnabledFilter(e.target.value as 'any' | 'true' | 'false')}
        >
          <MenuItem value="any">Any</MenuItem>
          <MenuItem value="true">Enabled</MenuItem>
          <MenuItem value="false">Disabled</MenuItem>
        </TextField>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="notification-blocks"
        title="Blocks"
        subtitle="Notification blocks — named, typed values referenced in templates as block.<codename>. A system block has no owning operator."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New block
            </Button>
          )
        }
      />

      <ResponsiveFilterBar items={toolbarItems} />

      <ResourceDataGrid<NotificationBlockListItem>
        queryKey={['notification_blocks']}
        queryFn={queryFn}
        columns={columns}
        extra={extra}
        batchResource="notification_blocks"
        batchActions={canManage ? [deleteBatchAction('console_notification_blocks_manage')] : []}
        defaultSort={{ field: 'codename', sort: 'asc' }}
        emptyMessage="No blocks yet."
        headerToolbar={{
          onResetAllFilters: resetAllFilters,
          resetDisabled: !anyFilterActive,
          search: true,
          searchPlaceholder: 'Codename, name…',
          exportFileName: 'notification-blocks',
        }}
      />

      {/* Create: block=null immediately. Edit: wait for the detail fetch. */}
      {dialogOpen && (editId === null || editQuery.data) && (
        <NotificationBlockFormDialog
          open={dialogOpen}
          block={editId === null ? null : (editQuery.data ?? null)}
          pageTenants={pageTenants}
          onClose={() => {
            setDialogOpen(false);
            setEditId(null);
          }}
        />
      )}
    </Box>
  );
}
